/**
 * Media Carousel — swipeable image/video carousel for posts.
 * Aspect ratio adapts to first media item.
 * Clamped: max portrait 9:16, max landscape 16:9.
 * All items share the same ratio from first item.
 */

import { useState, useRef, useCallback, memo } from 'react'
import { Play, Pause, ChevronLeft, ChevronRight } from 'lucide-react'

const VIDEO_EXTS = ['.mp4', '.mov', '.webm', '.ogg']
function isVideoUrl(url: string): boolean {
  const lower = url.toLowerCase().split('?')[0]
  return VIDEO_EXTS.some((ext) => lower.endsWith(ext))
}

// 9:16 = 0.5625 (max tall), 16:9 = 1.7778 (max wide)
const MIN_RATIO = 9 / 16
const MAX_RATIO = 16 / 9

function clampRatio(w: number, h: number): number {
  if (w <= 0 || h <= 0) return 1
  return Math.max(MIN_RATIO, Math.min(w / h, MAX_RATIO))
}

interface MediaCarouselProps {
  urls: string[]
}

export const MediaCarousel = memo(function MediaCarousel({
  urls,
}: MediaCarouselProps): React.ReactElement | null {
  const [current, setCurrent] = useState(0)
  const [ratio, setRatio] = useState<number | null>(null)
  const touchStartX = useRef(0)
  const touchDeltaX = useRef(0)
  const detected = useRef(false)

  const total = urls.length
  if (total === 0) return null

  const goTo = useCallback((index: number): void => {
    setCurrent(Math.max(0, Math.min(index, total - 1)))
  }, [total])

  const handleTouchStart = useCallback((e: React.TouchEvent): void => {
    touchStartX.current = e.touches[0].clientX
    touchDeltaX.current = 0
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent): void => {
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current
  }, [])

  const handleTouchEnd = useCallback((): void => {
    if (touchDeltaX.current < -50) goTo(current + 1)
    else if (touchDeltaX.current > 50) goTo(current - 1)
    touchDeltaX.current = 0
  }, [current, goTo])

  const onFirstImgLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>): void => {
    if (detected.current) return
    detected.current = true
    setRatio(clampRatio(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight))
  }, [])

  const onFirstVidLoad = useCallback((e: React.SyntheticEvent<HTMLVideoElement>): void => {
    if (detected.current) return
    detected.current = true
    setRatio(clampRatio(e.currentTarget.videoWidth, e.currentTarget.videoHeight))
  }, [])

  const aspectStyle = ratio ? { aspectRatio: String(ratio) } : { aspectRatio: '1' }

  // Single media
  if (total === 1) {
    const url = urls[0]
    return (
      <div className="w-full overflow-hidden rounded-xl bg-black/5" style={aspectStyle} data-carousel>
        {isVideoUrl(url) ? (
          <VideoPlayer url={url} onMeta={onFirstVidLoad} />
        ) : (
          <img src={url} alt="" loading="lazy" onLoad={onFirstImgLoad} className="w-full h-full object-cover" />
        )}
      </div>
    )
  }

  return (
    <div className="relative" data-carousel>
      <div
        className="overflow-hidden rounded-xl"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {urls.map((url, i) => (
            <div key={i} className="w-full shrink-0 bg-black/5" style={aspectStyle}>
              {isVideoUrl(url) ? (
                <VideoPlayer url={url} onMeta={i === 0 ? onFirstVidLoad : undefined} />
              ) : (
                <img
                  src={url}
                  alt=""
                  loading={i <= 1 ? 'eager' : 'lazy'}
                  onLoad={i === 0 ? onFirstImgLoad : undefined}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {current > 0 && (
        <button
          onClick={() => goTo(current - 1)}
          className="hidden lg:flex absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white/80 hover:bg-black/60 transition-colors"
        >
          <ChevronLeft size={18} />
        </button>
      )}
      {current < total - 1 && (
        <button
          onClick={() => goTo(current + 1)}
          className="hidden lg:flex absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white/80 hover:bg-black/60 transition-colors"
        >
          <ChevronRight size={18} />
        </button>
      )}

      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm text-[11px] font-medium text-white/90 tabular-nums">
        {current + 1}/{total}
      </div>

      {total <= 6 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {urls.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                i === current ? 'bg-white w-4' : 'bg-white/40'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
})

// ── Video Player ──

function VideoPlayer({ url, onMeta }: {
  url: string
  onMeta?: (e: React.SyntheticEvent<HTMLVideoElement>) => void
}): React.ReactElement {
  const [playing, setPlaying] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  const toggle = useCallback((): void => {
    const el = videoRef.current
    if (!el) return
    if (playing) el.pause()
    else void el.play()
    setPlaying(!playing)
  }, [playing])

  return (
    <div className="relative cursor-pointer w-full h-full" onClick={toggle}>
      <video
        ref={videoRef}
        src={url}
        className="w-full h-full object-cover"
        playsInline
        muted={!playing}
        loop
        preload="metadata"
        onLoadedMetadata={onMeta}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />
      <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
        playing ? 'opacity-0 hover:opacity-100' : 'opacity-100'
      }`}>
        <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
          {playing
            ? <Pause size={20} className="text-white" fill="white" />
            : <Play size={20} className="text-white ml-0.5" fill="white" />
          }
        </div>
      </div>
    </div>
  )
}
